// Karta "Progi cenowe" w expanded row pozycji oferty.
//
// Lokalny draft state — bez Formik. Walidacja Yup wywoływana synchronicznie
// w handleSave. Po sukcesie PUT React Query invalidate triggeruje refetch,
// useEffect synchronizuje draft z propsem (single source of truth = serwer).

import React, { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, CircularProgress, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ValidationError } from 'yup';
import { useQueryClient } from 'react-query';

import {
  IPriceThreshold,
  usePutSalesCrmOfferPosition
} from 'api';

import {
  isPriceDescending,
  thresholdsListSchema
} from './priceThresholdsSchema';
import PriceThresholdRow from './PriceThresholdRow';

import styles from 'theme/components/containers/TraderClientOfferProductsContainer/PriceThresholdsEditor.module.scss';

export interface IPriceThresholdsEditorProps {
  clientId: number;
  offerId: string;
  positionId: number;
  /** Cena netto pozycji — domyślna wartość dla nowego progu i opis empty state. */
  positionPriceNetSales: number;
  /** Ilość pozycji — do wyliczenia efektywnego progu i tooltipa badge'a. */
  positionQty: number;
  /** Jednostka miary produktu (np. "Sztuka", "Karton", "kg") — pokazywana jako suffix przy ilości progu. */
  positionUnit: string;
  /** Lista progów z serwera (`null`/brak pola = brak progów). */
  thresholds: IPriceThreshold[] | null | undefined;
  /** Tryb wariantowy z serwera — true = klient wybiera jeden próg przy akceptacji. */
  asVariants?: boolean;
  /** Wymuszona wersja oferty (historyczna). Steruje `readOnly`. */
  versionId?: number | null;
  /** Twardy readOnly — domyślnie aktywny gdy `versionId !== null`. */
  readOnly?: boolean;
}

// Lokalny typ z _key dla stabilnych React keys (UUID generowany przez crypto).
interface IDraftThreshold extends IPriceThreshold {
  _key: string;
}

const formatPriceLocal = (raw: number): string => {
  if (Number.isNaN(raw)) return '0,00 zł';
  return `${raw.toFixed(2).replace('.', ',')} zł`;
};

/**
 * Zaokrągla cenę do 2 miejsc po przecinku. Backend zwraca `priceNetSales` z pełną precyzją
 * float (np. 587.15555555556 dla wyniku dzielenia `totalNet/qty` w kalkulacji) — input progu
 * cenowego musi pokazać liczbę w postaci jaką handlowiec jest w stanie odczytać i edytować.
 */
const roundPrice = (raw: number): number => Math.round(raw * 100) / 100;

/** Generuje stabilny klucz wiersza. crypto.randomUUID dostępne w Chrome 92+/Safari 15.4+. */
const generateKey = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback dla starszych środowisk (test, jsdom bez crypto).
  return `tk-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const toDraft = (list: IPriceThreshold[] | null | undefined): IDraftThreshold[] =>
  (list ?? []).map((t) => ({ ...t, price: roundPrice(t.price), _key: generateKey() }));

const stripKey = (list: IDraftThreshold[]): IPriceThreshold[] =>
  list.map(({ qtyFrom, price }) => ({ qtyFrom, price: roundPrice(price) }));

const arraysEqual = (a: IPriceThreshold[], b: IPriceThreshold[]): boolean => {
  if (a.length !== b.length) return false;
  return a.every(
    (item, idx) => item.qtyFrom === b[idx].qtyFrom && item.price === b[idx].price
  );
};

const PriceThresholdsEditor: FC<IPriceThresholdsEditorProps> = ({
  clientId,
  offerId,
  positionId,
  positionPriceNetSales,
  positionQty,
  positionUnit,
  thresholds,
  asVariants = false,
  versionId = null,
  readOnly
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const effectiveReadOnly = readOnly ?? versionId !== null;

  const serverList = useMemo<IPriceThreshold[]>(() => thresholds ?? [], [thresholds]);

  const [draft, setDraft] = useState<IDraftThreshold[]>(() => toDraft(serverList));
  const [draftAsVariants, setDraftAsVariants] = useState<boolean>(asVariants);
  const [perRowErrors, setPerRowErrors] = useState<
    Record<string, { qtyFrom?: string; price?: string }>
  >({});
  const [listError, setListError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  // Sync draft z propsa po sukcesie PUT / initial / zmianie pozycji.
  useEffect(() => {
    setDraft(toDraft(serverList));
    setPerRowErrors({});
    setListError(null);
    setWarning(null);
  }, [serverList]);

  // Sync flagi wariantowej z propsa (po refetch lub zmianie pozycji).
  useEffect(() => {
    setDraftAsVariants(asVariants);
  }, [asVariants]);

  const { mutate: updatePosition, isLoading: isSaving } = usePutSalesCrmOfferPosition(
    clientId,
    offerId,
    {
      onSuccess: () => {
        // Inwalidujemy całe drzewo zapytań dla tej oferty (positions, totals, version data
        // z `useGetSalesCrmClientOffer` zawierające `valueNet`/`valueGross`, comments, services).
        // Bez segmentu 'positions' na końcu — to byłby zbyt wąski prefiks i pomijałby `value`
        // ekranu oferty (query key z segmentem 'version').
        queryClient.invalidateQueries([
          'sales-crm',
          'client',
          clientId,
          'offer',
          offerId
        ]);
      }
    }
  );

  const draftStripped = useMemo(() => stripKey(draft), [draft]);
  const isDirty =
    !arraysEqual(draftStripped, serverList) || draftAsVariants !== asVariants;
  const hasHardErrors =
    listError !== null ||
    Object.values(perRowErrors).some((e) => e.qtyFrom || e.price);
  const disableInputs = isSaving || effectiveReadOnly;

  // Wyznaczamy efektywny próg (najwyższy qtyFrom <= positionQty).
  const activeKey = useMemo<string | null>(() => {
    if (draft.length === 0) return null;
    const sorted = [...draft].sort((a, b) => a.qtyFrom - b.qtyFrom);
    let candidate: IDraftThreshold | null = null;
    for (const item of sorted) {
      if (item.qtyFrom <= positionQty) candidate = item;
    }
    return candidate?._key ?? null;
  }, [draft, positionQty]);

  // Recompute soft warning gdy draft się zmienia.
  useEffect(() => {
    if (draft.length < 2) {
      setWarning(null);
      return;
    }
    setWarning(
      isPriceDescending(stripKey(draft))
        ? null
        : t('Uwaga: ceny powinny maleć wraz ze wzrostem ilości.')
    );
  }, [draft, t]);

  const handleAddRow = useCallback(() => {
    setDraft((prev) => {
      const lastQty = prev.length === 0 ? 0 : Math.max(...prev.map((p) => p.qtyFrom));
      const nextQty = lastQty > 0 ? lastQty * 2 : 1;
      return [
        ...prev,
        {
          qtyFrom: nextQty,
          price: positionPriceNetSales > 0 ? roundPrice(positionPriceNetSales) : 0.01,
          _key: generateKey()
        }
      ];
    });
  }, [positionPriceNetSales]);

  const handleRowChange = useCallback(
    (key: string, patch: Partial<IPriceThreshold>) => {
      setDraft((prev) => prev.map((row) => (row._key === key ? { ...row, ...patch } : row)));
      setPerRowErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setListError(null);
    },
    []
  );

  const handleRemoveRow = useCallback((key: string) => {
    setDraft((prev) => prev.filter((row) => row._key !== key));
    setPerRowErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setListError(null);
  }, []);

  const handleCancel = useCallback(() => {
    setDraft(toDraft(serverList));
    setDraftAsVariants(asVariants);
    setPerRowErrors({});
    setListError(null);
    setWarning(null);
  }, [serverList, asVariants]);

  const handleSave = useCallback(() => {
    if (effectiveReadOnly || isSaving) return;

    const stripped = stripKey(draft);

    try {
      thresholdsListSchema.validateSync(stripped, { abortEarly: false });
    } catch (e) {
      if (e instanceof ValidationError) {
        const nextErrors: Record<string, { qtyFrom?: string; price?: string }> = {};
        let nextListError: string | null = null;
        e.inner.forEach((err) => {
          if (!err.path) {
            // top-level test (qtyFrom-ascending) — `path` jest pusty.
            nextListError = err.message;
            return;
          }
          // Path format: "[0].qtyFrom" lub "[1].price".
          const match = err.path.match(/^\[(\d+)\]\.(qtyFrom|price)$/);
          if (!match) return;
          const idx = parseInt(match[1], 10);
          const field = match[2] as 'qtyFrom' | 'price';
          const key = draft[idx]?._key;
          if (!key) return;
          nextErrors[key] = { ...nextErrors[key], [field]: err.message };
        });
        setPerRowErrors(nextErrors);
        setListError(nextListError);
      }
      return;
    }

    setPerRowErrors({});
    setListError(null);

    // Posortuj rosnąco po qtyFrom przed PUT.
    const payloadList = [...stripped].sort((a, b) => a.qtyFrom - b.qtyFrom);
    updatePosition({
      positionId,
      payload: {
        priceThresholds: payloadList,
        priceThresholdsAsVariants: draftAsVariants
      }
    });
  }, [draft, draftAsVariants, effectiveReadOnly, isSaving, positionId, updatePosition]);

  const renderEmptyState = () => (
    <div className={styles.emptyState}>
      <div className={styles.emptyStateText}>
        {t('Pozycja używa pojedynczej ceny netto: {{price}}', {
          price: formatPriceLocal(positionPriceNetSales)
        })}
      </div>
      {isDirty && !effectiveReadOnly && (
        <div className={styles.emptyStateDirtyNote}>
          {t('Masz niezapisane zmiany — kliknij Zapisz progi, żeby wyczyścić progi cenowe.')}
        </div>
      )}
      {!effectiveReadOnly && (
        <button
          type="button"
          className={styles.addRowBtn}
          onClick={handleAddRow}
          disabled={isSaving}>
          <AddIcon style={{ fontSize: 16 }} />
          {t('Dodaj pierwszy próg')}
        </button>
      )}
      {isDirty && !effectiveReadOnly && (
        <div className={styles.footerRow}>
          <button
            type="button"
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={hasHardErrors || isSaving}>
            {t('Zapisz progi')}
          </button>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={handleCancel}
            disabled={isSaving}>
            {t('Anuluj')}
          </button>
          {isSaving && (
            <span className={styles.savingHint}>
              <CircularProgress size={14} />
              {t('Zapisuję progi cenowe...')}
            </span>
          )}
        </div>
      )}
    </div>
  );

  const renderList = () => (
    <>
      <div className={styles.thresholdsList}>
        {draft.map((row) => (
          <PriceThresholdRow
            key={row._key}
            qtyFrom={row.qtyFrom}
            price={row.price}
            qtyFromError={perRowErrors[row._key]?.qtyFrom}
            priceError={perRowErrors[row._key]?.price}
            isActive={row._key === activeKey}
            positionQty={positionQty}
            positionUnit={positionUnit}
            disabled={disableInputs}
            readOnly={effectiveReadOnly}
            asVariants={draftAsVariants}
            onChange={(patch) => handleRowChange(row._key, patch)}
            onRemove={() => handleRemoveRow(row._key)}
          />
        ))}
      </div>

      {activeKey === null && draft.length > 0 && !draftAsVariants && (
        <div className={styles.belowMinNote}>
          {t('Poniżej najniższego progu — obowiązuje cena bazowa.')}
        </div>
      )}

      {!effectiveReadOnly && (
        <button
          type="button"
          className={styles.addRowBtn}
          onClick={handleAddRow}
          disabled={isSaving}>
          <AddIcon style={{ fontSize: 16 }} />
          {t('Dodaj próg')}
        </button>
      )}

      {listError && <div className={styles.listError}>{t(listError)}</div>}
      {warning && <div className={styles.warningText}>{warning}</div>}

      {draft.length > 0 && (
        <label className={styles.variantsToggle}>
          <Checkbox
            size="small"
            className={styles.variantsCheckbox}
            checked={draftAsVariants}
            disabled={disableInputs}
            onChange={(e) => setDraftAsVariants(e.target.checked)}
          />
          <span className={styles.variantsLabel}>
            {t('Traktuj progi jako warianty do wyboru (klient wybiera jeden)')}
          </span>
          <Tooltip
            title={t(
              'Opcja wyłączona (progi ilościowe): cena w wierszu to cena ZA SZTUKĘ od podanej ilości w górę. Klient zamawia dowolną ilość, a cena za sztukę zmienia się zależnie od osiągniętego progu. Opcja włączona (warianty do wyboru): cena w wierszu to cena CAŁKOWITA za podaną ilość. Klient akceptując ofertę wybiera konkretny próg (np. biorę 100 szt za 1000 zł albo 500 szt za 4000 zł) zamiast podawać własną ilość.'
            )}
            arrow
            placement="top">
            <InfoOutlinedIcon className={styles.variantsInfoIcon} />
          </Tooltip>
        </label>
      )}

      {!effectiveReadOnly && (
        <div className={styles.footerRow}>
          <button
            type="button"
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={!isDirty || hasHardErrors || isSaving}>
            {t('Zapisz progi')}
          </button>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={handleCancel}
            disabled={!isDirty || isSaving}>
            {t('Anuluj')}
          </button>
          {isSaving && (
            <span className={styles.savingHint}>
              <CircularProgress size={14} />
              {t('Zapisuję progi cenowe...')}
            </span>
          )}
        </div>
      )}
    </>
  );

  const showEmptyState = draft.length === 0;

  return (
    <div className={styles.priceThresholdsBody}>
      {showEmptyState ? renderEmptyState() : renderList()}
    </div>
  );
};

export default PriceThresholdsEditor;
